import { Callout, DropdownItem } from '@dev-spendesk/grapes';
import { type PayableVersion } from '@spendesk/bookkeeping-core-types';
import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { useNotifications } from 'modules/app/notifications';
import { useIsAnalyticalSplitActivated } from 'modules/bookkeep/apis/analytical-split';
import {
  isEditableCustomField,
  removeCustomFieldsSelectsWithNoValues,
} from 'modules/bookkeep/prepare-payables/components/PreparePayablesInbox/components/PreparePayablesFormFields/removeCustomFieldsSelectsWithNoValues';
import { useCreateCustomFieldMutation } from 'modules/bookkeep/prepare-payables/components/PreparePayablesInbox/hooks/useCreateCustomFieldMutation/useCreateCustomFieldMutation';
import {
  type CustomFieldValue,
  useValuesOfCustomFieldsQuery,
} from 'modules/bookkeep/prepare-payables/components/PreparePayablesInbox/hooks/useValuesOfCustomFieldsQuery';
import {
  isSelectInputConfig,
  type BooleanInputConfig,
  type InputValueEntry,
  type PreparePayablesInputConfig,
} from 'modules/bookkeep/prepare-payables/components/PreparePayablesInbox/panelInputConfig';
import {
  type Payable,
  type PayableType,
} from 'modules/bookkeep/prepare-payables/models';
import { useDesignatedCustomFieldQuery } from 'modules/custom-fields/hooks/useDesignatedCustomFieldQuery';
import { PayableInputComponent } from 'modules/payable/components/PayableInputComponent';
import {
  buildCustomFieldsInputsConfig,
  useGetPayableFieldValues,
} from 'modules/payable/hooks/api/useGetPayableFieldValues';
import {
  allRuleOutputsFromAutomation,
  type AnalyticalFieldAssociationAutomation,
} from 'modules/payable/models';
import { useFeature } from 'src/core/common/hooks/useFeature';
import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import { type FieldMappingOpenFor } from 'src/core/modules/accounting-integration/hooks/useAnalyticalFieldOpenFor';
import { useHasMapSync } from 'src/core/modules/accounting-integration/hooks/useHasMapSync';
import { type AnalyticalFieldMappingV2 } from 'src/core/modules/bookkeep';
import { AutomationIcon as AnalyticalFieldAssociationAutomationIcon } from 'src/core/modules/payable/components/AutomationIcon';

import { unwrapQuery } from '../../../../api/unwrapQuery';
import { AutomationConflictSelector } from '../AutomationConflictSelector';
import { PayableRuleAutomationExistsCallout } from '../PayableRuleAutomationExistsCallout/PayableRuleAutomationExistsCallout';
import { shouldPayableRuleAutomationExistsCalloutBeDisplayed } from '../PayableRuleAutomationExistsCallout/shouldPayableRuleAutomationExistsCalloutBeDisplayed';
import { PayableUnmappedAnalyticalFieldCallout } from '../PayableUnmappedAnalyticalFieldCallout/PayableUnmappedAnalyticalFieldCallout';
import { ItemSyncStatus } from './ItemSyncStatus/ItemSyncStatus';

export const PayableCustomFields = ({
  payable,
  customFieldsInputsConfig,
  customFields,
  customFieldsMappings,
  errors,
  onChange,
  onMapUnmappedValue,
  spacing = '24',
  editSplitValues = false,
}: {
  payable: Pick<Payable, 'id' | 'version' | 'type' | 'automation'>;
  customFieldsInputsConfig: PreparePayablesInputConfig[];
  customFields: Record<string, string | undefined>;
  customFieldsMappings: AnalyticalFieldMappingV2[];
  errors: Record<string, string | undefined>;
  onChange: (key: string, value: string | boolean | null) => void;
  onMapUnmappedValue: (arguments_: FieldMappingOpenFor) => void;
  spacing?: '16' | '24' | '32' | '0';
  editSplitValues?: boolean;
}) => {
  const { t } = useTranslation('global');
  const { dangerNotif } = useNotifications();
  const hasMapSyncFeature = useHasMapSync();

  const useDesignatedCustomFieldIdQuery = useDesignatedCustomFieldQuery();

  const valuesOfCustomFieldsQuery = useValuesOfCustomFieldsQuery({
    customFieldValuesIds: Object.values(customFields).filter(
      (value) => typeof value === 'string',
    ),
    customFieldIds: Object.keys(customFields),
  });

  const valuesOfCustomFields =
    valuesOfCustomFieldsQuery.status === 'success'
      ? valuesOfCustomFieldsQuery.data
      : new Map<string, CustomFieldValue>();

  const getCustomFieldWarningMessage = (
    inputConfig: PreparePayablesInputConfig,
  ) => {
    if (inputConfig.type === 'select') {
      const selectedCustomFieldValue = inputConfig.values.find(
        (value) => value.key === customFields[inputConfig.id],
      );
      if (
        selectedCustomFieldValue &&
        selectedCustomFieldValue.archiveDate !== null
      ) {
        return t('expenseInbox.expenseEditor.customFieldDeleted');
      }
    }
  };

  const visibleCustomFields = removeCustomFieldsSelectsWithNoValues(
    customFieldsInputsConfig,
  ).filter((input) => isEditableCustomField(input, editSplitValues));

  const [hasFixedRuleConflict, setHasFixedRuleConflict] = useState(new Set());

  useEffect(() => {
    const conflictingIds = Object.keys(errors).filter(
      (key) =>
        errors[key] ===
        'expenseInbox.expenseEditor.analyticalFieldValueNeedsMapping',
    );

    setHasFixedRuleConflict(new Set(conflictingIds));
  }, [errors]);

  return visibleCustomFields.map((inputConfig) => {
    const analyticalFieldAssociationAutomation = getCustomFieldAutomation({
      analyticalFieldAssociationAutomation:
        payable.automation?.analyticalFieldAssociations,
      customFieldId: inputConfig.id,
    });

    const valueId = customFields[inputConfig.id] as string; // TODO @financeAccountant boolean boolean custom fields not handled yet

    const isCurrentlyAutomatedValue = getIsCurrentlyAutomatedCustomFieldValue(
      valueId,
      analyticalFieldAssociationAutomation,
    );

    const isPrefilledByRuleBasedAutomation =
      analyticalFieldAssociationAutomation?.kind === 'ruleBased';

    const hasUnappliedRuleBasedAutomation =
      analyticalFieldAssociationAutomation?.isAppliedOnPayable === false &&
      analyticalFieldAssociationAutomation?.kind === 'ruleBased';

    const visibleRuleBasedAutomation =
      isCurrentlyAutomatedValue &&
      isPrefilledByRuleBasedAutomation &&
      analyticalFieldAssociationAutomation.output === valueId
        ? analyticalFieldAssociationAutomation
        : undefined;

    const designatedCustomFieldResponse = unwrapQuery(
      useDesignatedCustomFieldIdQuery,
    );

    const designatedCustomFieldId =
      designatedCustomFieldResponse?.customFieldId;

    const fieldMapping = customFieldsMappings.find(
      (m) => m.internalId === inputConfig.id,
    );
    const valuesMapping = fieldMapping?.values ?? [];
    const getMappingForValue = (mappingValueId: string) =>
      valuesMapping?.find((m) => m.internalId === mappingValueId);

    const currentValueMapping = getMappingForValue(valueId);

    const validationError = errors[inputConfig.id];
    // When it's a mapping error, we need to show the error outline and block form submit but not show any
    // message so we have to intercept it here.
    const isMappingRequiredError =
      validationError ===
      'expenseInbox.expenseEditor.analyticalFieldValueNeedsMapping';

    return (
      <div key={inputConfig.id} className={`flex flex-col mb-${spacing}`}>
        <PayableInputComponent
          input={{
            ...inputConfig,
            'data-testid': `standalone-${inputConfig.id}`,
            inputVariant:
              isCurrentlyAutomatedValue && !isPrefilledByRuleBasedAutomation
                ? 'magicGradient'
                : 'default',
            renderPrefix: () =>
              isCurrentlyAutomatedValue && (
                <AnalyticalFieldAssociationAutomationIcon
                  automation={analyticalFieldAssociationAutomation}
                  rule={
                    analyticalFieldAssociationAutomation &&
                    ruleForValueId(
                      analyticalFieldAssociationAutomation,
                      valueId,
                    )
                  }
                  t={t}
                />
              ),
            renderOption: (
              option: InputValueEntry,
              state: { isSelected: boolean; isHighlighted: boolean },
            ) => {
              const mappedValue = getMappingForValue(option.key);

              return (
                <DropdownItem
                  key={option.key}
                  label={option.label}
                  isSelected={state.isSelected && !option.archiveDate}
                  isHighlighted={state.isHighlighted}
                  isDisabled={!!option.archiveDate}
                  suffix={
                    option.archiveDate ? (
                      t('misc.deleted')
                    ) : (
                      <>
                        {analyticalFieldAssociationAutomation && (
                          <AnalyticalFieldAssociationAutomationIcon
                            automation={analyticalFieldAssociationAutomation}
                            rule={ruleForValueId(
                              analyticalFieldAssociationAutomation,
                              option.key,
                            )}
                            t={t}
                          />
                        )}
                        {hasMapSyncFeature && fieldMapping && (
                          <ItemSyncStatus
                            isMapped={!!mappedValue}
                            value={mappedValue?.externalName ?? ''}
                          />
                        )}
                      </>
                    )
                  }
                />
              );
            },
            isInvalid: isMappingRequiredError || !!validationError,
          }}
          allowNewValue={inputConfig.allowNewValue}
          error={isMappingRequiredError ? undefined : validationError}
          value={customFields[inputConfig.id] ?? undefined}
          warningMessage={getCustomFieldWarningMessage(inputConfig)}
          onChange={(value) => {
            // Do not trigger change if the new value and the previous value are both null
            if (value === null && !customFields[inputConfig.id]) {
              return;
            }
            onChange(inputConfig.id, value);
          }}
          onCreateNewValue={async (newValue) => {
            if (!inputConfig.onCreateNewValue) {
              return '';
            }
            try {
              return await inputConfig.onCreateNewValue(newValue);
            } catch (error) {
              dangerNotif(
                t('expenseInbox.errors.customFieldValueCreationError', {
                  value: newValue,
                  field: inputConfig.name,
                }),
              );

              throw error;
            }
          }}
        />
        {hasUnappliedRuleBasedAutomation &&
          shouldPayableRuleAutomationExistsCalloutBeDisplayed({
            payable: { version: payable.version as PayableVersion },
            currentValue: valueId,
            ruleBasedAutomation: analyticalFieldAssociationAutomation,
          }) && (
            <PayableRuleAutomationExistsCallout<'customField'>
              payableIds={[payable.id]}
              currentValue={
                isBooleanCustomField(customFieldsInputsConfig, inputConfig.id)
                  ? getDisplayNameForBooleanCustomField(
                      customFieldsInputsConfig,
                      inputConfig.id,
                      customFields[inputConfig.id],
                      t,
                    )
                  : valuesOfCustomFields.get(valueId)?.name || ''
              }
              payableRule={analyticalFieldAssociationAutomation.rule}
              onUpdate={(output) => {
                onChange(
                  inputConfig.id,
                  output instanceof Object ? output[inputConfig.id] : output,
                );
              }}
              translateOutputValue={(output) => {
                const [_, value] = Object.entries(output).find(
                  // find the custom field id
                  ([key]) => key === inputConfig.id,
                ) ?? ['', ''];

                if (value instanceof Object) {
                  if (value.value instanceof Object) {
                    if (typeof value.value.name === 'boolean') {
                      return value.value.name ? t('misc.yes') : t('misc.no');
                    }
                    return value.value.name;
                  }
                  return value.value;
                }

                return value ?? '';
              }}
              appliedOnProperty={
                inputConfig.id === designatedCustomFieldId
                  ? 'expenseCategory'
                  : 'customField'
              }
              {...(analyticalFieldAssociationAutomation.conflict && {
                ruleConflict: analyticalFieldAssociationAutomation.conflict,
                conflictSelectorCalloutProps: {
                  ruleConflict: analyticalFieldAssociationAutomation.conflict,
                  selectedRule: analyticalFieldAssociationAutomation.rule,
                  onSelect: (output) => {
                    onChange(inputConfig.id, output);
                  },
                },
              })}
            />
          )}
        {visibleRuleBasedAutomation?.conflict &&
          !hasFixedRuleConflict.has(inputConfig.id) && (
            <AutomationConflictSelector
              selectedRule={visibleRuleBasedAutomation.rule}
              ruleConflict={visibleRuleBasedAutomation.conflict}
              onSelect={(output) => {
                setHasFixedRuleConflict(
                  hasFixedRuleConflict.add(inputConfig.id),
                );
                onChange(inputConfig.id, output);
              }}
            />
          )}
        {valuesOfCustomFields.get(customFields?.[inputConfig.id] as string)
          ?.archiveDate && (
          <Callout
            variant="warning"
            className="my-8 mt-4"
            title={t('expenseInbox.expenseEditor.customFieldValueDeleted', {
              value: valuesOfCustomFields.get(
                customFields?.[inputConfig.id] as string,
              )?.name,
            })}
          />
        )}
        {hasMapSyncFeature &&
          fieldMapping &&
          valueId &&
          !currentValueMapping &&
          !(
            visibleRuleBasedAutomation?.conflict &&
            !hasFixedRuleConflict.has(inputConfig.id)
          ) && (
            <PayableUnmappedAnalyticalFieldCallout
              onToMappingClick={() =>
                onMapUnmappedValue({
                  internalField: {
                    id: inputConfig.id,
                    name: inputConfig.name,
                  },
                  fieldType: 'customField',
                  targetValueId: valueId,
                  internalValues: isSelectInputConfig(inputConfig)
                    ? inputConfig.values.map(({ key, label }) => ({
                        id: key,
                        name: label,
                      }))
                    : [],
                })
              }
              isAlert={isMappingRequiredError}
            />
          )}
      </div>
    );
  });
};

/**
 * Helpers
 */

const getCustomFieldAutomation = ({
  analyticalFieldAssociationAutomation = [],
  customFieldId,
}: {
  analyticalFieldAssociationAutomation?: AnalyticalFieldAssociationAutomation[];
  customFieldId: string;
}) => {
  return analyticalFieldAssociationAutomation.find(
    ({ fieldEntityId }) => customFieldId === fieldEntityId,
  );
};

const getIsCurrentlyAutomatedCustomFieldValue = (
  currentValue: string,
  analyticalFieldAssociationAutomation?: AnalyticalFieldAssociationAutomation,
) => {
  return (
    analyticalFieldAssociationAutomation?.isAppliedOnPayable &&
    analyticalFieldAssociationAutomation?.fieldEntityValueId === currentValue
  );
};

export const useCustomFieldsInputsConfigFn = () => {
  const { t } = useTranslation('global');

  const companyId = useCompanyId();
  const hasCustomFieldsFeature = useFeature(FEATURES.CUSTOM_FIELDS);

  const fieldValuesQuery = useGetPayableFieldValues();
  const [createCustomField] = useCreateCustomFieldMutation(); // check if this is the correct mutation
  const queryClient = useQueryClient();

  const designatedCustomFieldQueryState = useDesignatedCustomFieldQuery();

  const expenseCategoryCustomFieldId =
    designatedCustomFieldQueryState.status === 'success'
      ? designatedCustomFieldQueryState.data.customFieldId
      : undefined;

  const isAnalyticalSplitActivated = useIsAnalyticalSplitActivated();

  return hasCustomFieldsFeature && fieldValuesQuery.status === 'success'
    ? (
        payableType: PayableType,
        teamId: string | null | undefined,
        customFields: Record<string, string | undefined>,
      ) =>
        buildCustomFieldsInputsConfig({
          customFields: fieldValuesQuery.data.customFields.sort((a) =>
            a.id === expenseCategoryCustomFieldId ? -1 : 0,
          ),
          companyId,
          queryClient,
          createCustomField,
          customFieldsValues: Object.fromEntries(
            Object.entries(customFields).filter(([, value]) => value !== null),
          ) as Record<string, string | boolean>,
          teamId: teamId ?? undefined,
          expenseTypes: [payableType],
          expenseCategoryCustomFieldId,
          translator: t,
          isAnalyticalSplitActivated,
        })
    : undefined;
};

export const useCustomFieldsInputsConfig = ({
  payableType,
  teamId,
  customFields,
}: {
  payableType: PayableType;
  teamId: string | null | undefined;
  customFields: Record<string, string | undefined>;
}) => {
  const function_ = useCustomFieldsInputsConfigFn();
  return function_ ? function_(payableType, teamId, customFields) : [];
};

const ruleForValueId = (
  automation: AnalyticalFieldAssociationAutomation,
  valueId?: string,
) => {
  return allRuleOutputsFromAutomation(automation).find(
    ({ output }) => output === valueId,
  )?.rule;
};

function isBooleanCustomField(
  inputConfig: PreparePayablesInputConfig[],
  currentCustomField: string,
): boolean {
  return inputConfig.some(
    (c) => c.id === currentCustomField && c.type === 'boolean',
  );
}

function getDisplayNameForBooleanCustomField(
  inputConfig: PreparePayablesInputConfig[],
  currentCustomField: string,
  currentValue: string | undefined,
  t: TGlobalFunctionTyped,
): string {
  const maybeBooleanCF = inputConfig.find(
    (c) => c.id === currentCustomField && c.type === 'boolean',
  ) as BooleanInputConfig | undefined;

  if (!maybeBooleanCF || !currentValue) {
    return '';
  }

  const maybeValue = maybeBooleanCF.values?.find((v) => v.key === currentValue);
  if (!maybeValue) {
    return '';
  }

  return maybeValue.label === 'true' ? t('misc.yes') : t('misc.no');
}
